
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createAdminUser, verifyUserExists } from '@/utils/auth/adminUtils';
import { toast } from 'sonner';
import ErrorDisplay from '@/components/auth/ErrorDisplay';

const AdminSetup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [email, setEmail] = useState('careerguidekerala@gmail.com');
  const [password, setPassword] = useState('doItNow@25');
  const [name, setName] = useState('Admin User');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // Check if admin user already exists
  useEffect(() => {
    const checkAdminUser = async () => {
      try {
        setIsChecking(true);
        const exists = await verifyUserExists(email);
        setUserExists(exists);
      } catch (error) {
        console.error('Error checking admin user:', error);
        toast.error('Failed to check if admin user exists');
      } finally {
        setIsChecking(false);
      }
    };
    
    checkAdminUser();
  }, [email]);
  
  const handleCreateAdminUser = async () => {
    try {
      setError('');
      setIsLoading(true);
      
      if (!email || !password || !name) {
        setError('Please fill in all fields');
        setIsLoading(false);
        return;
      }
      
      const result = await createAdminUser(
        email,
        password,
        name
      );
      
      if (result.success) {
        toast.success(result.message);
        // Admin user was created successfully, redirect to login page
        navigate('/administrator/login');
      } else {
        setError(result.message);
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Error creating admin user:', error);
      setError(`Failed to create admin user: ${error.message || 'Unknown error'}`);
      toast.error(`Failed to create admin user: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Admin Setup</CardTitle>
            <CardDescription>Checking admin user status...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Admin Setup | Career Guide Kerala</title>
      </Helmet>
      
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Admin User Setup</CardTitle>
            <CardDescription className="text-center">
              {userExists 
                ? 'Admin user already exists. You can proceed to login.' 
                : 'Create the admin user account to access the admin panel'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-4">
              <ErrorDisplay error={error} />
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input 
                  id="name" 
                  type="text" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Admin User"
                />
              </div>
              
              {userExists && (
                <div className="bg-yellow-50 p-4 rounded-md">
                  <p className="text-yellow-700">
                    An account with this email already exists. You can try with a different email or proceed to login.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-2">
            {!userExists ? (
              <Button 
                className="w-full bg-brand-blue hover:bg-brand-blue/90"
                onClick={handleCreateAdminUser}
                disabled={isLoading || !email || !password || !name}
              >
                {isLoading ? 'Creating Admin User...' : 'Create Admin User'}
              </Button>
            ) : (
              <Button 
                className="w-full bg-brand-blue hover:bg-brand-blue/90"
                onClick={() => navigate('/administrator/login')}
              >
                Go to Login
              </Button>
            )}
            
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    </>
  );
};

export default AdminSetup;
